import { useLocation, useNavigate } from 'react-router'
import { useMediaQuery } from '@mantine/hooks'
import clsx from 'clsx'
import { IconBell, IconChevronLeft, IconGitCompare } from '@tabler/icons-react'
import { AspectRatio, Button, Image } from '@mantine/core'
import { getCode } from 'country-list'
import Meta from '../../common/Meta'
import classes from './PlayerComponents.module.css'
import { Player } from '../api'
import { Typography } from '../../common/typography'
import * as flags from 'country-flag-icons/react/3x2'

export default function PlayerHeader({
  player,
  competitionId,
}: {
  player: Player
  competitionId?: string
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const navigate = useNavigate()
  const location = useLocation()

  const fields = player.fields as Record<string, string | number | boolean>
  const hasCountry = fields && 'country' in fields
  const hasPosition = fields && 'position' in fields

  const countryCode =
    hasCountry && typeof fields.country === 'string'
      ? (getCode(fields.country)?.toUpperCase() ?? null)
      : null
  const isPlaceholderPhoto = /\.svg$/i.test(player.photo.main)

  if (isDesktop) {
    return (
      <div className={classes.header_container}>
        <Meta title={player.name} />
        <div className={classes.header_container_wrapper}>
          <IconChevronLeft
            className={classes.back_icon}
            onClick={() =>
              navigate(
                location.state?.from?.pathname ||
                  `/competitions/${competitionId}/players`
              )
            }
          />

          <Button
            className={classes.compare_button}
            leftSection={<IconGitCompare className={classes.compare_icon} />}
            onClick={() =>
              navigate(
                `/competitions/${competitionId}/compare?ltype=user&lid=${player.user_id ?? 0}`
              )
            }
          >
            Compare
          </Button>
        </div>
        <div>
          <div className={classes.player_header_container}>
            <div className={classes.player_header_wrapper}>
              <AspectRatio ratio={1 / 1} className={classes.aspect_ratio}>
                <Image
                  src={player.photo.main}
                  alt={player.name}
                  className={classes.player_image}
                  fit={isPlaceholderPhoto ? 'contain' : 'scale-down'}
                />
                <AspectRatio
                  ratio={1 / 1}
                  className={classes.team_logo_container}
                >
                  <Image
                    src={player.team_logo.mini}
                    alt={player.name}
                    fit="scale-down"
                  />
                </AspectRatio>
              </AspectRatio>
              <div className={classes.title_container}>
                <Typography
                  fs="sm"
                  fc={{ light: 'white', dark: 'white' }}
                  lh="140"
                >
                  {player.team_name}
                </Typography>
                <Typography
                  fs="xxl"
                  fw={700}
                  fc={{ light: 'white', dark: 'white' }}
                  lh="100"
                >
                  {player.name}
                </Typography>
              </div>
            </div>
            <div className={classes.header_widgets_wrapper}>
              {hasPosition && (
                <div className={classes.headerBox}>
                  <Typography fs="sm" fw={500} lh="130" ta="center">
                    Role Position
                  </Typography>
                  <Typography fs="sm" lh="150" ta="center">
                    {fields.position}
                  </Typography>
                </div>
              )}
              {countryCode &&
                flags[countryCode as keyof typeof flags] &&
                (() => {
                  const FlagComponent = flags[countryCode as keyof typeof flags]
                  return (
                    <div className={classes.headerBox}>
                      <Typography fs="sm" fw={500} lh="130" ta="center">
                        Country
                      </Typography>
                      <FlagComponent className={classes.flagIcon} />
                    </div>
                  )
                })()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container_wrapper_mobile}>
      <Meta title={player.name} />
      <div
        className={clsx(classes.player_header_container_mobile, {
          [classes.withProfileFields]: hasCountry || hasPosition,
        })}
      >
        <div className={classes.header_container_wrapper}>
          <IconChevronLeft
            className={clsx(classes.back_icon, {
              [classes.back_icon_mobile]: !isDesktop,
            })}
            onClick={() =>
              navigate(
                location.state?.from?.pathname ||
                  `/competitions/${competitionId}/players`
              )
            }
          />

          <div className={classes.notifications_container}>
            <Button
              className={clsx(classes.compare_button, {
                [classes.compare_button_mobile]: !isDesktop,
              })}
              leftSection={
                <IconGitCompare
                  className={clsx(classes.compare_icon, {
                    [classes.compare_icon_mobile]: !isDesktop,
                  })}
                />
              }
              onClick={() =>
                navigate(
                  `/competitions/${competitionId}/compare?ltype=user&lid=${player.user_id ?? 0}`
                )
              }
            >
              Compare
            </Button>
            <IconBell className={classes.notifications_icon} />
          </div>
        </div>
        <div
          className={clsx(classes.player_header_wrapper, {
            [classes.player_header_wrapper_mobile]: !isDesktop,
          })}
        >
          <AspectRatio ratio={1 / 1} className={classes.aspect_ratio}>
            <Image
              src={player.photo.main}
              alt={player.name}
              className={classes.player_image}
              fit="scale-down"
            />
            <div className={classes.team_logo_container}>
              <Image
                src={player.team_logo.mini}
                alt={player.name}
                className={classes.team_logo}
                fit="scale-down"
              />
            </div>
          </AspectRatio>
          <div className={classes.title_container}>
            <Typography fs="sm" fc={{ light: 'white', dark: 'white' }} lh="140">
              {player.team_name}
            </Typography>
            <Typography
              fs="xxl"
              fw={700}
              fc={{ light: 'white', dark: 'white' }}
              lh="100"
            >
              {player.name}
            </Typography>
          </div>
        </div>
      </div>
      {(hasPosition || countryCode) && (
        <div className={classes.header_widgets_wrapper_mobile}>
          {hasPosition && (
            <div className={classes.headerBox}>
              <Typography fw={500} lh="130" ta="center">
                Role Position
              </Typography>
              <Typography lh="150" ta="center">
                {fields.position}
              </Typography>
            </div>
          )}
          {countryCode &&
            flags[countryCode as keyof typeof flags] &&
            (() => {
              const FlagComponent = flags[countryCode as keyof typeof flags]
              return (
                <div className={classes.headerBox}>
                  <Typography fw={500} lh="130" ta="center">
                    Country
                  </Typography>
                  <FlagComponent className={classes.flagIcon} />
                </div>
              )
            })()}
        </div>
      )}
    </div>
  )
}
